import React, { useEffect, useRef, useState } from 'react'
import { Search, FileText, Plus, CornerDownLeft } from 'lucide-react'
import { cn } from '../../utils/cn'
import { workspaceAPI } from '../../services/api'

/**
 * CommandPalette — ⌘K. Instant title/excerpt matches from the local index,
 * merged with server full-text hits (body matches show their snippet).
 */
export default function CommandPalette({ open, onClose, index, onOpenNote, onCreateNote }) {
  const [q, setQ] = useState('')
  const [serverHits, setServerHits] = useState([])
  const [idx, setIdx] = useState(0)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (open) {
      setQ(''); setServerHits([]); setIdx(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    clearTimeout(debounceRef.current)
    if (!q.trim() || q.trim().length < 2) { setServerHits([]); return }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await workspaceAPI.search(q.trim())
        setServerHits(data.hits || [])
      } catch { setServerHits([]) }
    }, 220)
    return () => clearTimeout(debounceRef.current)
  }, [q])

  if (!open) return null

  const needle = q.trim().toLowerCase()
  const localHits = needle
    ? index.filter((n) => (n.title || '').toLowerCase().includes(needle) || (n.excerpt || '').toLowerCase().includes(needle))
    : index.slice(0, 8)
  const seen = new Set(localHits.map((n) => n.id))
  const bodyHits = serverHits.filter((h) => !seen.has(h.id))
  const results = [...localHits.slice(0, 8), ...bodyHits.slice(0, 8)]
  const showCreate = needle.length > 0
  const total = results.length + (showCreate ? 1 : 0)

  const choose = (n) => {
    if (n === 'create') onCreateNote(q.trim())
    else onOpenNote(n.id)
    onClose()
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose() }
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(total - 1, i + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)) }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (idx < results.length) choose(results[idx])
      else if (showCreate) choose('create')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4">
      <div className="absolute inset-0 bg-ink-950/30 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-2xl bg-white border border-ink-100 shadow-elev-4 overflow-hidden animate-scale-in">
        <div className="flex items-center gap-3 px-4 border-b border-ink-100">
          <Search className="w-4 h-4 text-ink-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setIdx(0) }}
            onKeyDown={onKeyDown}
            placeholder="Search notes, or type to create…"
            className="w-full py-3.5 text-[15px] text-ink-900 placeholder:text-ink-300 outline-none bg-transparent"
          />
          <kbd className="ws-kbd shrink-0">esc</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-1.5">
          {results.map((n, i) => (
            <button
              key={n.id}
              onClick={() => choose(n)}
              onMouseEnter={() => setIdx(i)}
              className={cn('w-full flex items-start gap-3 px-4 py-2.5 text-left', i === idx ? 'bg-accent-50' : '')}
            >
              <FileText className="w-4 h-4 text-ink-300 mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink-800 truncate">{n.title || 'Untitled'}</span>
                <span className="block text-xs text-ink-400 truncate">{n.snippet || n.excerpt || '—'}</span>
              </span>
              <span className="text-[10px] text-ink-300 shrink-0 mt-1">{n.collection}</span>
            </button>
          ))}
          {showCreate && (
            <button
              onClick={() => choose('create')}
              onMouseEnter={() => setIdx(results.length)}
              className={cn('w-full flex items-center gap-3 px-4 py-2.5 text-left', idx === results.length ? 'bg-accent-50' : '')}
            >
              <span className="flex items-center justify-center w-4 h-4 shrink-0">
                <Plus className="w-4 h-4 text-accent-600" />
              </span>
              <span className="text-sm text-ink-700">
                Create note “<span className="font-semibold text-ink-900">{q.trim()}</span>”
              </span>
              <CornerDownLeft className="w-3.5 h-3.5 text-ink-300 ml-auto" />
            </button>
          )}
          {!results.length && !showCreate && (
            <div className="px-4 py-6 text-center text-sm text-ink-400">Start typing to search your workspace</div>
          )}
        </div>
      </div>
    </div>
  )
}
